import { z } from "zod";

export const UrlSchema = z.string().url().brand<"Url">();
/**
 * An absolute URL string, branded after validation so an unchecked string
 * cannot be passed where a real URL is required. Library output is already
 * branded; to brand untrusted input, use `parseUrl` from `data-model/parse.ts`.
 */
export type Url = z.infer<typeof UrlSchema>;
/**
 * Brand a string as Url WITHOUT runtime validation. Use only when the caller
 * has already proved the value is a valid absolute URL (e.g. it came from
 * `new URL().toString()` or from a Zod-validated source). For untrusted input,
 * use `parseUrl` from `data-model/parse.ts` instead.
 */
export function unsafeBrandUrl(input: string): Url {
  return input as Url;
}

export const ChapterIndexSchema = z.number().int().nonnegative().brand<"ChapterIndex">();
/**
 * A chapter's position in the table of contents, branded as a non-negative
 * integer. Indices are **0-based**: chapter 1 is index 0. Build one with
 * {@link chapterIndex}.
 */
export type ChapterIndex = z.infer<typeof ChapterIndexSchema>;
/** Brand a number as ChapterIndex without validation. See `unsafeBrandUrl`. */
export function unsafeBrandChapterIndex(input: number): ChapterIndex {
  return input as ChapterIndex;
}
/**
 * Build a validated ChapterIndex from a plain number. Throws if `input` is not
 * a non-negative integer. Used internally and by custom adapters when hand-
 * building a {@link ChapterRef}; `DownloadOptions.chapterRange` takes plain
 * numbers, so callers do not need this for ranges. Indices are 0-based.
 */
export function chapterIndex(input: number): ChapterIndex {
  return ChapterIndexSchema.parse(input);
}

export const AdapterIdSchema = z.string().min(1).brand<"AdapterId">();
/** Stable identifier for a site adapter (e.g. `"truyenfull"`), branded as a non-empty string. */
export type AdapterId = z.infer<typeof AdapterIdSchema>;
/** Brand a string as AdapterId without validation. See `unsafeBrandUrl`. */
export function unsafeBrandAdapterId(input: string): AdapterId {
  return input as AdapterId;
}

export const IsoDateSchema = z.string().datetime().brand<"IsoDate">();
/** An ISO-8601 datetime string (e.g. `"2026-06-17T09:30:00.000Z"`), branded after validation. */
export type IsoDate = z.infer<typeof IsoDateSchema>;
/** Brand a string as IsoDate without validation. See `unsafeBrandUrl`. */
export function unsafeBrandIsoDate(input: string): IsoDate {
  return input as IsoDate;
}

export const ResumeTokenSchema = z.string().min(1).brand<"ResumeToken">();
/**
 * Opaque token returned on a partial download (see {@link DownloadPartial}).
 * Pass it back via `DownloadOptions.resume` to continue where the run left off.
 * Branded as a non-empty string.
 */
export type ResumeToken = z.infer<typeof ResumeTokenSchema>;
/** Brand a string as ResumeToken without validation. See `unsafeBrandUrl`. */
export function unsafeBrandResumeToken(input: string): ResumeToken {
  return input as ResumeToken;
}
